import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BookOpen, ArrowLeft, Edit, Save, X, Trash2, Lock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import PageHeader from '@/components/PageHeader'
import MarkdownView from '@/components/MarkdownView'
import { wikiAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { can } from '@/lib/permissions'

const IS_NEW = 'nieuw'
const CATEGORY_OPTIONS = [
  // Deelnemer (zichtbaar voor iedereen)
  { value: 'beginnen',        label: 'Deelnemer · Beginnen' },
  { value: 'toernooien',      label: 'Deelnemer · Toernooien' },
  { value: 'compo',           label: 'Deelnemer · Compo\'s' },
  { value: 'punten-prijzen',  label: 'Deelnemer · Punten & prijzen' },
  { value: 'bar-kassa',       label: 'Deelnemer · Bar & kassa' },
  { value: 'teams',           label: 'Deelnemer · Teams' },
  { value: 'hulp',            label: 'Deelnemer · Hulp' },
  // Admin (alleen admin ziet)
  { value: 'lan-organisatie', label: 'Admin · LAN-organisatie' },
  { value: 'toernooi-admin',  label: 'Admin · Toernooi-beheer' },
  { value: 'hardware-midi',   label: 'Admin · Hardware & MIDI' },
  { value: 'obs-streaming',   label: 'Admin · OBS & streaming' },
  { value: 'servers-infra',   label: 'Admin · Servers & infra' },
  { value: 'homelab',         label: 'Admin · Homelab' },
]

export default function WikiArticlePage() {
  const { slug }        = useParams()
  const nav             = useNavigate()
  const { user }        = useAuth()
  const canEdit         = can(user?.role, 'manage_users')
  const isNew           = slug === IS_NEW

  const [article, setArticle] = useState(null)
  const [loading, setLoading] = useState(!isNew)
  const [error, setError]     = useState(null)

  const [editing, setEditing] = useState(isNew)
  const [form, setForm]       = useState({
    title: '', category: 'tournament-formats', summary: '', content_md: '', visibility: 'all'
  })
  const [saving, setSaving]   = useState(false)

  useEffect(() => {
    if (isNew) return
    setLoading(true)
    wikiAPI.get(slug)
      .then(r => {
        setArticle(r.data?.article)
        setForm({
          title:      r.data?.article?.title      || '',
          category:   r.data?.article?.category   || 'general',
          summary:    r.data?.article?.summary    || '',
          content_md: r.data?.article?.content_md || '',
          visibility: r.data?.article?.visibility || 'all',
        })
      })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [slug, isNew])

  async function handleSave() {
    if (!form.title.trim()) { setError('Titel is verplicht'); return }
    setSaving(true)
    setError(null)
    try {
      if (isNew) {
        const r = await wikiAPI.create(form)
        nav(`/wiki/${r.data.article.slug}`, { replace: true })
      } else {
        const r = await wikiAPI.update(slug, form)
        setArticle(r.data.article)
        setEditing(false)
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!confirm('Artikel definitief verwijderen?')) return
    try {
      await wikiAPI.remove(slug)
      nav('/wiki', { replace: true })
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Laden…</p>
  if (!isNew && !article && error) {
    return (
      <div>
        <PageHeader icon={BookOpen} title="Wiki" />
        <Card><CardContent className="p-6 text-sm text-signal">{error}</CardContent></Card>
        <div className="mt-3"><BackLink /></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-3"><BackLink /></div>

      <PageHeader
        icon={BookOpen}
        title={
          <span className="inline-flex items-center gap-2">
            {isNew ? 'Nieuw artikel' : (article?.title || 'Wiki')}
            {article?.visibility === 'admin' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-signal/40 bg-signal/10 text-signal text-[10px] font-mono uppercase tracking-widest">
                <Lock className="h-3 w-3" /> Alleen admin
              </span>
            )}
          </span>
        }
        subtitle={!isNew && article?.summary}
        actions={canEdit && !editing && !isNew ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Edit className="h-4 w-4 mr-1" />Bewerken
            </Button>
            <Button size="sm" variant="outline" onClick={handleDelete}
                    className="text-signal hover:text-signal">
              <Trash2 className="h-4 w-4 mr-1" />Verwijder
            </Button>
          </div>
        ) : null}
      />

      {error && <p className="text-sm text-signal mb-3">{error}</p>}

      {editing ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <Label htmlFor="w-title">Titel</Label>
              <Input id="w-title" value={form.title}
                     onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="w-cat">Categorie</Label>
              <select id="w-cat"
                      value={form.category}
                      onChange={e => setForm({ ...form, category: e.target.value })}
                      className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
                {CATEGORY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="w-sum">Korte samenvatting</Label>
              <Input id="w-sum" value={form.summary}
                     onChange={e => setForm({ ...form, summary: e.target.value })}
                     placeholder="Één zin die het artikel samenvat" />
            </div>
            <div className="flex items-center justify-between gap-4 py-2 border-y border-border/40">
              <div className="min-w-0">
                <div className="font-medium text-sm">Zichtbaarheid</div>
                <div className="text-xs text-muted-foreground">
                  {form.visibility === 'admin'
                    ? 'Alleen admins zien dit artikel.'
                    : 'Iedereen die is ingelogd ziet dit artikel.'}
                </div>
              </div>
              <div className="flex gap-1">
                <button type="button"
                        onClick={() => setForm({ ...form, visibility: 'all' })}
                        className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded border transition-colors ${
                          form.visibility === 'all'
                            ? 'bg-acid text-background border-acid'
                            : 'border-border text-muted-foreground hover:border-acid/40'
                        }`}>
                  Iedereen
                </button>
                <button type="button"
                        onClick={() => setForm({ ...form, visibility: 'admin' })}
                        className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded border transition-colors ${
                          form.visibility === 'admin'
                            ? 'bg-signal text-background border-signal'
                            : 'border-border text-muted-foreground hover:border-signal/40'
                        }`}>
                  Alleen admin
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="w-body">Inhoud (markdown)</Label>
              <Textarea id="w-body"
                        value={form.content_md}
                        onChange={e => setForm({ ...form, content_md: e.target.value })}
                        rows={20}
                        className="font-mono text-xs" />
              <p className="text-xs text-muted-foreground mt-1">
                Ondersteund: # / ## / ### headings, **bold**, *italic*, `code`, [link](url),
                blockquotes (&gt;), lijsten (-), code-blocks (```).
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-1" />{saving ? 'Opslaan…' : 'Opslaan'}
              </Button>
              <Button size="sm" variant="outline"
                      onClick={() => {
                        if (isNew) nav('/wiki')
                        else       setEditing(false)
                      }}>
                <X className="h-4 w-4 mr-1" />Annuleer
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-5">
            <MarkdownView source={article?.content_md} />
            {article?.author_name && (
              <div className="mt-6 pt-3 border-t border-border text-xs text-muted-foreground font-mono uppercase tracking-widest">
                Auteur: {article.author_name}
                {article.updated_at && <> — bijgewerkt {new Date(article.updated_at).toLocaleDateString('nl-BE')}</>}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function BackLink() {
  return (
    <Link to="/wiki" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-acid">
      <ArrowLeft className="h-3 w-3" />Terug naar wiki
    </Link>
  )
}
